"use client";

import { useEffect, useState } from "react";

import Link from "next/link";

import { useRouter } from "next/navigation";

import { ChevronLeft, ChevronRight } from "lucide-react";

import { OptimizedProductImage } from "@/components/product/OptimizedProductImage";
import { QuickviewModal } from "@/components/ui/QuickviewModal";
import { useHorizontalScroll } from "@/hooks/useHorizontalScroll";
import { prepareBrandCategoryNavigation } from "@/lib/brand-navigation";
import {
  BRAND_CATEGORY_ALL,
  fetchBrandTopCategoriesViaBridge,
  fetchFeaturedProductsViaBridge,
  type BridgeBrandTopCategory,
} from "@/lib/brand-private-bridge-client";
import { buildBrandShopPath } from "@/lib/brand-routing";
import { cn } from "@/lib/cn";
import { buildCanonicalProductPathFromRecord } from "@/lib/product-url";
import { getProductImage, getFormattedPrice, type Product } from "@/lib/product-utils";
import { SECTION_PADDING_X } from "@/styles/responsive-spacing";

import type { SearchProduct } from "openapi/shop_agent/client/types.gen";

interface BrandItemsSectionProps {
  brandName: string;
  brandSlug?: string;
  /**
   * Server-rendered products for the "All" tab. When present the client never
   * fetches for that tab; when absent it reads the featured-products bridge.
   */
  initialFeaturedProducts?: Product[] | null;
  /** Server-rendered category tabs. When absent the client fetches them. */
  initialTopCategories?: BridgeBrandTopCategory[] | null;
}

// Category data with level information for building facet queries
interface CategoryData {
  name: string;
  level: number;
}

function toCategoryData(categories: BridgeBrandTopCategory[]): CategoryData[] {
  return categories.map((cat) => ({ name: cat.category_name, level: cat.category_level }));
}

export default function BrandItemsSection({
  brandName,
  brandSlug,
  initialFeaturedProducts,
  initialTopCategories,
}: BrandItemsSectionProps) {
  const hasInitialProducts = Array.isArray(initialFeaturedProducts);
  const hasInitialCategories = Array.isArray(initialTopCategories);
  const [products, setProducts] = useState<SearchProduct[]>(
    hasInitialProducts ? (initialFeaturedProducts as SearchProduct[]) : []
  );
  const [loading, setLoading] = useState(!hasInitialProducts);
  const [productsFailed, setProductsFailed] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(BRAND_CATEGORY_ALL);
  const [categories, setCategories] = useState<CategoryData[]>(
    hasInitialCategories ? toCategoryData(initialTopCategories as BridgeBrandTopCategory[]) : []
  );
  const [categoriesLoading, setCategoriesLoading] = useState(!hasInitialCategories);
  const router = useRouter();
  const [expandedProduct, setExpandedProduct] = useState<Product | null>(null);

  // Horizontal scroll navigation
  const {
    scrollRef: scrollContainerRef,
    canScrollLeft,
    canScrollRight,
    scrollLeft,
    scrollRight,
    scrollToStart,
  } = useHorizontalScroll({
    threshold: 20, // Account for -mx-4 px-4 padding pattern
    viewportMultiplier: 0.4, // Scroll by ~40% on desktop
    deps: [products.length], // Recalculate when products load
  });

  // Reset scroll position and category when brand changes
  useEffect(() => {
    scrollToStart();
    setSelectedCategory(BRAND_CATEGORY_ALL);
  }, [brandName, scrollToStart]);

  // Fetch brand top categories from API (only when not server-rendered)
  useEffect(() => {
    if (hasInitialCategories) return;
    let cancelled = false;

    const fetchBrandCategories = async () => {
      if (!brandName) {
        setCategoriesLoading(false);
        return;
      }

      try {
        setCategoriesLoading(true);
        const response = await fetchBrandTopCategoriesViaBridge(brandName, 2, 3);
        if (cancelled) return;

        if (response?.top_categories && response.top_categories.length > 0) {
          setCategories(toCategoryData(response.top_categories));
        } else {
          setCategories([]);
        }
      } catch (error) {
        if (cancelled) return;
        console.error("Error fetching brand categories:", error);
        setCategories([]);
      } finally {
        if (!cancelled) {
          setCategoriesLoading(false);
        }
      }
    };

    fetchBrandCategories();

    return () => {
      cancelled = true;
    };
  }, [brandName]);

  // "All" tab: server-rendered products when available, otherwise the same
  // brand-grid browse through the featured-products bridge, with no category.
  //
  // Deliberately independent of `categories`: the previous single effect
  // depended on the categories request and fired a second identical fetch when
  // it resolved, doubling the search load per page view.
  useEffect(() => {
    if (selectedCategory !== BRAND_CATEGORY_ALL) return;
    if (!brandName) {
      setLoading(false);
      return;
    }
    if (hasInitialProducts) {
      setProducts(initialFeaturedProducts as SearchProduct[]);
      setProductsFailed(false);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setProductsFailed(false);
    fetchFeaturedProductsViaBridge({ brandName, limit: 8 })
      .then((result) => {
        if (!cancelled) setProducts(result);
      })
      .catch((error) => {
        console.error("Error fetching brand products:", error);
        if (!cancelled) {
          setProducts([]);
          setProductsFailed(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [brandName, selectedCategory, hasInitialProducts, initialFeaturedProducts]);

  // Category tabs: a human-selected facet search. Waits for the category list so
  // the facet level is known; the "All" tab never enters this effect.
  useEffect(() => {
    if (selectedCategory === BRAND_CATEGORY_ALL || categoriesLoading || !brandName) return;
    let cancelled = false;
    const categoryData = categories.find((c) => c.name === selectedCategory);
    setLoading(true);
    setProductsFailed(false);
    fetchFeaturedProductsViaBridge({
      brandName,
      categoryName: selectedCategory,
      categoryLevel: categoryData?.level,
      limit: 8,
    })
      .then((result) => {
        if (!cancelled) setProducts(result);
      })
      .catch((error) => {
        console.error("Error fetching products:", error);
        if (!cancelled) {
          setProducts([]);
          setProductsFailed(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [brandName, selectedCategory, categoriesLoading, categories]);

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
  };

  const formatCategoryName = (name: string) => {
    if (name === BRAND_CATEGORY_ALL) return "All";
    return name
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const displayCategories = [BRAND_CATEGORY_ALL, ...categories.map((c) => c.name)];

  const handleViewAll = () => {
    const categoryData = categories.find((c) => c.name === selectedCategory);
    const categoryLevel = categoryData?.level ?? 1;
    const threadId = prepareBrandCategoryNavigation(brandName, selectedCategory, categoryLevel);
    router.push(`/results/${threadId}`);
  };

  return (
    <section className="mb-16 md:mb-20" data-testid="brand-featured-items-section">
      {/* Header with Title and Navigation Arrows */}
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-title text-ink">Featured Items</h2>

        {/* Navigation Arrows - only show when scrolling is possible */}
        {(canScrollLeft || canScrollRight) && (
          <div className="flex gap-2">
            <button
              onClick={scrollLeft}
              disabled={!canScrollLeft}
              className={cn(
                "flex h-10 w-10 items-center justify-center rounded-full bg-white transition-all",
                canScrollLeft
                  ? "text-ink hover:bg-surface-muted"
                  : "cursor-not-allowed text-ink-faint"
              )}
              aria-label="Previous products"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={scrollRight}
              disabled={!canScrollRight}
              className={cn(
                "flex h-10 w-10 items-center justify-center rounded-full bg-white transition-all",
                canScrollRight
                  ? "text-ink hover:bg-surface-muted"
                  : "cursor-not-allowed text-ink-faint"
              )}
              aria-label="Next products"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>

      {/* Category Tabs */}
      <div className="mb-8 border-b-2 border-gray-200">
        <div className="-mb-0.5 flex gap-6 overflow-x-auto md:gap-8">
          {categoriesLoading ? (
            <div className="flex gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-5 w-20 animate-pulse rounded bg-gray-200" />
              ))}
            </div>
          ) : (
            displayCategories.map((category) => (
              <button
                key={category}
                onClick={() => handleCategoryChange(category)}
                className={cn(
                  "whitespace-nowrap border-b-2 pb-2.5 text-[12px] tracking-wider transition-colors",
                  selectedCategory === category
                    ? "border-ink text-ink"
                    : "border-transparent text-ink-muted hover:text-ink-secondary"
                )}
              >
                {formatCategoryName(category)}
              </button>
            ))
          )}
        </div>
      </div>

      {/* Product Carousel - 2x2 grid pages on mobile, 5-column scroll on desktop */}
      <div
        ref={scrollContainerRef}
        className="mb-8 flex snap-x snap-mandatory gap-4 overflow-x-auto pb-2 md:gap-4"
        style={{
          marginLeft: `calc(-1 * ${SECTION_PADDING_X.RESPONSIVE})`,
          marginRight: `calc(-1 * ${SECTION_PADDING_X.RESPONSIVE})`,
          paddingLeft: SECTION_PADDING_X.RESPONSIVE,
          paddingRight: SECTION_PADDING_X.RESPONSIVE,
          // Ensure snap points align with content padding
          scrollPaddingLeft: SECTION_PADDING_X.RESPONSIVE,
          scrollPaddingRight: SECTION_PADDING_X.RESPONSIVE,
        }}
      >
        {loading ? (
          <>
            {/* Mobile: Show one 2x2 grid page skeleton */}
            <div className="grid w-full flex-shrink-0 grid-cols-2 gap-4 md:hidden">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="space-y-3">
                  <div className="aspect-[3/4] animate-pulse rounded-3xl bg-gray-100" />
                  <div className="space-y-2">
                    <div className="h-4 w-3/4 animate-pulse rounded bg-gray-100" />
                    <div className="h-4 w-1/3 animate-pulse rounded bg-gray-100" />
                  </div>
                </div>
              ))}
            </div>
            {/* Desktop: Show 5 skeleton items */}
            <div className="hidden md:flex md:w-full md:gap-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="w-[calc(20%-12.8px)] flex-shrink-0 space-y-3">
                  <div className="aspect-[3/4] animate-pulse rounded-3xl bg-gray-100" />
                  <div className="space-y-2">
                    <div className="h-4 w-3/4 animate-pulse rounded bg-gray-100" />
                    <div className="h-4 w-1/3 animate-pulse rounded bg-gray-100" />
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : products.length > 0 ? (
          <>
            {/* Mobile: 2x2 grid pages */}
            {Array.from({ length: Math.ceil(products.length / 4) }).map((_, pageIndex) => (
              <div
                key={`page-${pageIndex}`}
                className="grid w-full flex-shrink-0 snap-start grid-cols-2 gap-4 md:hidden"
              >
                {products.slice(pageIndex * 4, pageIndex * 4 + 4).map((product, index) => {
                  const imageUrl = getProductImage(product as unknown as Product);
                  const productName = product.name || "Unnamed Product";
                  const priceInfo = getFormattedPrice(product);
                  const productPath = buildCanonicalProductPathFromRecord(
                    product as unknown as Record<string, unknown>,
                    String(product.id ?? "")
                  );

                  return (
                    <div
                      key={product.id || `${pageIndex}-${index}`}
                      className="group space-y-3"
                      data-testid="brand-featured-item"
                    >
                      <button
                        type="button"
                        aria-label={`Quick view ${productName}`}
                        data-testid="brand-featured-quickview"
                        onClick={() => setExpandedProduct(product as unknown as Product)}
                        className="relative block aspect-[3/4] w-full overflow-hidden rounded-3xl bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
                      >
                        {imageUrl && imageUrl !== "/placeholder.jpg" ? (
                          <OptimizedProductImage
                            fill
                            sizes="(max-width: 767px) 50vw, 20vw"
                            src={imageUrl}
                            alt={productName}
                            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center text-gray-400">
                            <svg
                              className="h-12 w-12"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                              />
                            </svg>
                          </div>
                        )}
                      </button>
                      <div className="space-y-1">
                        <h3 className="line-clamp-2 text-body font-medium text-ink">
                          {productPath ? (
                            <Link
                              href={productPath}
                              prefetch={false}
                              data-testid="brand-featured-product-link"
                              className="hover:underline focus-visible:underline"
                            >
                              {productName}
                            </Link>
                          ) : (
                            productName
                          )}
                        </h3>
                        {priceInfo.final && (
                          <p className="text-body text-ink-secondary">
                            {priceInfo.final}
                            {priceInfo.original && (
                              <span className="ml-2 text-ink-muted line-through">
                                {priceInfo.original}
                              </span>
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
            {/* Desktop: Individual scrollable items */}
            {products.map((product, index) => {
              const imageUrl = getProductImage(product as unknown as Product);
              const productName = product.name || "Unnamed Product";
              const priceInfo = getFormattedPrice(product);
              const productPath = buildCanonicalProductPathFromRecord(
                product as unknown as Record<string, unknown>,
                String(product.id ?? "")
              );

              return (
                <div
                  key={product.id || index}
                  className="hidden w-[calc(20%-12.8px)] flex-shrink-0 snap-start space-y-3 md:block"
                  data-testid="brand-featured-item"
                >
                  <button
                    type="button"
                    aria-label={`Quick view ${productName}`}
                    data-testid="brand-featured-quickview"
                    onClick={() => setExpandedProduct(product as unknown as Product)}
                    className="group relative block aspect-[3/4] w-full overflow-hidden rounded-3xl bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
                  >
                    {imageUrl && imageUrl !== "/placeholder.jpg" ? (
                      <OptimizedProductImage
                        fill
                        sizes="(max-width: 767px) 50vw, 20vw"
                        src={imageUrl}
                        alt={productName}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-gray-400">
                        <svg
                          className="h-12 w-12"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                    )}
                  </button>
                  <div className="space-y-1">
                    <h3 className="line-clamp-2 text-body font-medium text-ink">
                      {productPath ? (
                        <Link
                          href={productPath}
                          prefetch={false}
                          data-testid="brand-featured-product-link"
                          className="hover:underline focus-visible:underline"
                        >
                          {productName}
                        </Link>
                      ) : (
                        productName
                      )}
                    </h3>
                    {priceInfo.final && (
                      <p className="text-body text-ink-secondary">
                        {priceInfo.final}
                        {priceInfo.original && (
                          <span className="ml-2 text-ink-muted line-through">
                            {priceInfo.original}
                          </span>
                        )}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </>
        ) : (
          <div className="w-full py-12 text-center text-body text-ink-muted">
            {productsFailed
              ? "Products are temporarily unavailable. Please try again."
              : "No products available"}
          </div>
        )}
      </div>

      {/* View All Products Link */}
      {(selectedCategory === BRAND_CATEGORY_ALL || products.length > 0) && (
        <div className="text-center">
          {selectedCategory === BRAND_CATEGORY_ALL ? (
            <Link
              href={buildBrandShopPath({ brandName, brandSlug })}
              prefetch={false}
              data-testid="brand-view-all-products-link"
              className="text-body text-ink underline decoration-ink-faint underline-offset-4 transition-colors hover:decoration-ink"
            >
              View All Products
            </Link>
          ) : (
            <button
              type="button"
              onClick={handleViewAll}
              className="text-body text-ink underline decoration-ink-faint underline-offset-4 transition-colors hover:decoration-ink"
            >
              View All Products
            </button>
          )}
        </div>
      )}

      {/* Quickview Modal */}
      {expandedProduct && (
        <QuickviewModal
          product={expandedProduct}
          isOpen={true}
          onClose={() => setExpandedProduct(null)}
        />
      )}
    </section>
  );
}
